// Вкладка «Долги»: за что человек должен, сколько осталось и что ему выдавали.
//
// До сих пор долги человека в кабинете было видно только в общей сводке — то есть на вопрос
// «сколько на нём висит формы» приходилось отвечать перебором отчёта. Здесь они рядом с самим
// человеком, вместе с историей выдач и кнопкой выдать новую.
//
// Выдача формы — денежное действие: она сразу заводит долг с графиком «тысяча в месяц».
// Поэтому оформление стоит рядом с долгами, а не в отдельном «складском» разделе: тот, кто
// выдаёт куртку, решает, что удержат из зарплаты.

import { useState } from 'react';

import { RiHandCoinLine, RiTShirtLine } from '@remixicon/react';
import { Link } from 'react-router';

import type { ApiError } from '@/shared/errors/api-error';
import type { UniformLineIn } from '@/shared/api/endpoints/losses';
import type { EmployeeOut } from '@/shared/api/endpoints/people';
import { useAuthStore } from '@/shared/auth/store';
import { formatKopecks, formatRubles } from '@/shared/money/format';
import { parseRublesToKopecks } from '@/shared/money/parse';
import { useLegalEntitiesQuery } from '@/shared/org/queries';
import { can } from '@/shared/rbac/permissions';
import { countLabel } from '@/shared/text/plural';
import { formatDateOnly } from '@/shared/time/format';
import * as Badge from '@/shared/ui/badge';
import { Block } from '@/shared/ui/block';
import { CheckboxField } from '@/shared/ui/checkbox-field';
import { DateField } from '@/shared/ui/date-field';
import { ErrorAlert } from '@/shared/ui/error-alert';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextField } from '@/shared/ui/text-field';
import { TextArea } from '@/shared/ui/text-area';
import * as Button from '@/shared/ui/button';
import * as Hint from '@/shared/ui/hint';
import * as LinkButton from '@/shared/ui/link-button';
import { EmptyState, ScreenState, Skeleton } from '@/shared/ui/screen-state';

import type { СостояниеГрафика } from '@/shared/debts/ПоляГрафика';
import { useEmployeeDebts } from '@/shared/debts/queries';
import {
  АДРЕС_ПЕРЕНОСА,
  useПереносДолгов,
  пустоУЧеловека,
  состояниеПереноса,
} from '@/shared/debts/перенос';
import { ПоляГрафика, ПУСТОЙ_ГРАФИК, разобратьГрафик } from '@/shared/debts/ПоляГрафика';
import { useDebtScheduleTypes } from '@/shared/dictionaries/debt-schedule-types';
import { useDebtTypes } from '@/shared/dictionaries/debt-types';

import { WriteOffDialog } from './WriteOffDialog';
import {
  useCreateDebt,
  useIssueUniform,
  useRegisterEarlyLeave,
  useTicketState,
  useUniformIssues,
  useUniformItems,
} from '../queries';

/** Оформление выдачи — то же право, что на долги: она заводит долг. */
const ISSUE_PERMISSION = 'core.debt.edit';
const WRITE_OFF_PERMISSION = 'core.debt.write_off';

const СОСТОЯНИЯ: Readonly<Record<string, string>> = {
  active: 'Действует',
  repaid: 'Погашен',
  restructured: 'Реструктурирован',
  suspended: 'Приостановлен',
  written_off: 'Списан',
  external_collection: 'Передан на взыскание',
  annulled: 'Аннулирован',
};

/** Сегодняшний день по часам человека, в виде «ГГГГ-ММ-ДД». */
function сегодняМестный(): string {
  const сейчас = new Date();
  const месяц = String(сейчас.getMonth() + 1).padStart(2, '0');
  const день = String(сейчас.getDate()).padStart(2, '0');
  return `${сейчас.getFullYear()}-${месяц}-${день}`;
}

/** Текст отказа сервера для человека. Конверт ошибки уже несёт причину и следующий шаг. */
function текстОтказа(ошибка: unknown, префикс: string): string {
  const конверт = (ошибка as { envelope?: { message?: string; next_action?: string } })?.envelope;
  const причина = конверт?.message ?? (ошибка instanceof Error ? ошибка.message : '');
  const шаг = конверт?.next_action ?? '';
  return [`${префикс}: ${причина || 'сервер отказал'}`, шаг].filter(Boolean).join('. ');
}

export function DebtsTab({ employee }: { employee: EmployeeOut }) {
  const долги = useEmployeeDebts(employee.id);
  // Пустой список здесь бывает двух видов: «ничего не должен» и «старые долги сюда ещё не
  // переносили». 23.09.2026 заказчик прочёл первое там, где было второе, — и был прав.
  const перенос = состояниеПереноса(useПереносДолгов());
  const пусто = пустоУЧеловека(перенос);
  const [заводим, setЗаводим] = useState(false);
  const [отказДолга, setОтказДолга] = useState<string | null>(null);
  const выдачи = useUniformIssues(employee.id);
  const справочник = useUniformItems();
  const выдать = useIssueUniform(employee.id);

  const права = useAuthStore((state) => state.permissions);
  const можно = can(права, ISSUE_PERMISSION);
  // Списание — отдельное право, и оно не у финотдела: это признание потери денег.
  const можноСписать = can(права, WRITE_OFF_PERMISSION);

  const билет = useTicketState(employee.id);
  const отметить = useRegisterEarlyLeave(employee.id);

  const [оформляем, setОформляем] = useState(false);
  const [отъезд, setОтъезд] = useState(false);
  // Местный день, а не по Гринвичу: в Усинске в семь утра `toISOString()` даёт ещё вчерашнюю
  // дату, и отъезд записывался бы вчерашним числом.
  const [дата, setДата] = useState(() => сегодняМестный());
  const [причина, setПричина] = useState('');
  const [подтверждена, setПодтверждена] = useState(false);
  const [набор, setНабор] = useState<Record<string, number>>({});
  // Покупная позиция: сценарий приёмки 43 требует «добавлена покупная позиция с ручной суммой».
  // Дверь её принимала (`title_manual`, `price_manual`), а в форме её не было: выдать можно
  // было только то, что заведено в справочнике, а куртку из магазина — нет.
  const [своиПозиции, setСвоиПозиции] = useState<
    { id: string; название: string; цена: string; количество: number }[]
  >([]);
  const [замечание, setЗамечание] = useState<string | null>(null);

  const позиции = справочник.data?.items ?? [];
  const итогСправочника = позиции.reduce(
    (сумма, позиция) => сумма + позиция.price * (набор[позиция.id] ?? 0),
    0,
  );
  const итогСвоих = своиПозиции.reduce((сумма, строка) => {
    const разбор = parseRublesToKopecks(строка.цена);
    return сумма + (разбор.ok ? разбор.value * строка.количество : 0);
  }, 0);
  const итог = итогСправочника + итогСвоих;

  function изменить(id: string, на: number) {
    setНабор((прежний) => {
      const количество = Math.max(0, (прежний[id] ?? 0) + на);
      const новый = { ...прежний };
      if (количество === 0) {
        delete новый[id];
      } else {
        новый[id] = количество;
      }
      return новый;
    });
  }

  async function оформить() {
    if (!можно) {
      setЗамечание(
        'Выдачу оформляет финансовый отдел или администратор: она заводит долг человеку. Попросите их оформить выдачу.',
      );
      return;
    }
    const строки: UniformLineIn[] = Object.entries(набор).map(([id, количество]) => ({
      uniform_item_id: id,
      quantity: количество,
    }));
    for (const своя of своиПозиции) {
      if (своя.название.trim() === '') {
        setЗамечание('У покупной позиции нет названия: в долге она встанет безымянной строкой.');
        return;
      }
      const разбор = parseRublesToKopecks(своя.цена);
      if (!разбор.ok || разбор.value <= 0) {
        setЗамечание(`Цена «${своя.название.trim()}» пишется числом: 2 500 или 2 500,50.`);
        return;
      }
      строки.push({
        title_manual: своя.название.trim(),
        price_manual: разбор.value,
        quantity: своя.количество,
      });
    }
    if (строки.length === 0) {
      setЗамечание('Выберите хотя бы одну позицию: выдача без позиций не заводит долга.');
      return;
    }
    setЗамечание(null);
    try {
      // Юрлицо здесь не выбирают: его берёт сервер из действующего назначения человека. В
      // карточке юрлица нет вовсе — оно принадлежит назначению, а не человеку.
      await выдать.mutateAsync({ employee_id: employee.id, lines: строки });
    } catch (ошибка) {
      // Отказ сервера обязан дойти до человека словами. Без этого он видит, что нажатие
      // «ничего не сделало», и жмёт ещё раз — теперь уже осознанно создавая вторую попытку.
      setЗамечание(текстОтказа(ошибка, 'Выдача не оформлена'));
      return;
    }
    setНабор({});
    setСвоиПозиции([]);
    setОформляем(false);
  }

  async function отметитьОтъезд() {
    if (!можно) {
      setЗамечание(
        'Отметку о досрочном отъезде ставит финансовый отдел или администратор: от неё зависит компенсация билета.',
      );
      return;
    }
    if (причина.trim().length < 10) {
      setЗамечание(
        'Опишите причину подробнее: от неё зависит, сохранится ли компенсация пяти тысяч, и через полгода это придётся объяснять.',
      );
      return;
    }
    setЗамечание(null);
    try {
      await отметить.mutateAsync({
        left_at: дата,
        reason: причина.trim(),
        reason_confirmed: подтверждена,
      });
    } catch (ошибка) {
      setЗамечание(текстОтказа(ошибка, 'Отметка не сохранена'));
      return;
    }
    setОтъезд(false);
    setПричина('');
    setПодтверждена(false);
  }

  const состояниеБилета = билет.data;

  return (
    <div className='flex flex-col gap-4 p-5'>
      {/* Билет стоит первым: это единственный долг, судьба которого зависит от решения
          человека, а не от графика. Уехал без подтверждения — вся сумма разом. */}
      {состояниеБилета !== undefined && состояниеБилета.has_ticket ? (
        <section className='flex flex-col gap-2 rounded-xl border border-stroke-soft-200 bg-bg-white-0 px-4 py-3'>
          <div className='flex items-baseline justify-between gap-3'>
            <span className='text-label-sm text-text-strong-950'>Билет</span>
            <span className='tabular-nums text-label-sm text-text-strong-950'>
              {formatRubles(состояниеБилета.outstanding)}
            </span>
          </div>
          <span className='text-paragraph-xs text-text-sub-600'>{состояниеБилета.explanation}</span>
          <div className='flex flex-wrap items-center gap-2'>
            <Badge.Root
              variant='lighter'
              color={состояниеБилета.deduct_at_once ? 'orange' : 'gray'}
              size='medium'
            >
              {состояниеБилета.deduct_at_once ? 'Удержится разом' : 'Удержание по графику'}
            </Badge.Root>
            {состояниеБилета.compensation_applied ? (
              <Badge.Root variant='lighter' color='green' size='medium'>
                Компенсация {formatRubles(состояниеБилета.compensation_amount)}
              </Badge.Root>
            ) : null}
            {состояниеБилета.compensation_used ? (
              <Badge.Root variant='lighter' color='gray' size='medium'>
                Компенсация уже использована
              </Badge.Root>
            ) : null}
          </div>

          {отъезд ? (
            <div className='flex flex-col gap-3 border-t border-stroke-soft-200 pt-3'>
              <DateField
                id='билет-дата-отъезда'
                label='Когда уехал'
                value={дата}
                onChange={setДата}
              />
              <TextArea
                id='билет-причина'
                label='Причина отъезда'
                value={причина}
                onChange={setПричина}
                rows={2}
                placeholder='Что произошло и чем подтверждено'
              />
              <CheckboxField
                id='билет-подтверждена'
                label='Причина подтверждена документом и решением заказчика'
                checked={подтверждена}
                onCheckedChange={setПодтверждена}
              />
              <span className='text-paragraph-xs text-text-sub-600'>
                Без подтверждения компенсация не применяется, и остаток билета удерживается одной
                суммой: следующего расчёта у уехавшего не будет.
              </span>
              <div className='flex gap-2'>
                <Button.Root
                  type='button'
                  variant='primary'
                  mode='filled'
                  size='xsmall'
                  onClick={() => void отметитьОтъезд()}
                >
                  {отметить.isPending ? 'Отмечаем…' : 'Отметить отъезд'}
                </Button.Root>
                <Button.Root
                  type='button'
                  variant='neutral'
                  mode='ghost'
                  size='xsmall'
                  onClick={() => setОтъезд(false)}
                >
                  Отмена
                </Button.Root>
              </div>
            </div>
          ) : (
            <Button.Root
              type='button'
              variant='neutral'
              mode='stroke'
              size='xsmall'
              onClick={() => {
                setОтъезд(true);
                setЗамечание(null);
              }}
            >
              Отметить досрочный отъезд
            </Button.Root>
          )}
        </section>
      ) : null}

      {/* Блок тот же, что в сводке и настройках: рамка с заголовком. Пока это был просто
          заголовок над пустым местом, две секции вкладки выглядели двумя абзацами текста, а
          не двумя разными списками. */}
      <Block
        flush
        title='Долги'
        action={
          // Долги обычно заводятся сами — приёмкой заявки в категории с признаком «создаёт
          // долг» либо выдачей формы по справочнику. Ручной путь нужен для того, что через
          // заявку не проходит: штраф, ремонт машины, заём от заказчика. Сценарий 41 обещает
          // его прямо, дверь на сервере есть, а в программе её не звал никто.
          <Button.Root
            type='button'
            variant='neutral'
            mode='stroke'
            size='xsmall'
            onClick={() => {
              if (!можно) {
                setОтказДолга(
                  'Долг заводит финансовый отдел или администратор: это удержание из зарплаты. Попросите их завести.',
                );
                return;
              }
              setОтказДолга(null);
              setЗаводим((было) => !было);
            }}
          >
            {заводим ? 'Не заводить' : 'Завести долг'}
          </Button.Root>
        }
      >
        {отказДолга !== null && (
          <div className='px-4 pt-3'>
            <Hint.Root>{отказДолга}</Hint.Root>
          </div>
        )}
        {заводим && <ФормаДолга employee={employee} onГотово={() => setЗаводим(false)} />}
        <ScreenState
          data={долги.data}
          error={долги.error as ApiError | null}
          subject='долги человека'
          isEmpty={(данные) => данные.items.length === 0}
          onRetry={() => void долги.refetch()}
          loading={<Skeleton className='m-4 h-20' />}
          empty={
            <div className='p-5'>
              <EmptyState
                icon={RiHandCoinLine}
                title={пусто.title}
                description={пусто.description}
                action={
                  // Прямой переход к переносу: 24.09.2026 заказчик прочёл «не перенесены» и не
                  // нашёл, где переносить, — в карточке человека пути туда не было.
                  перенос === 'не перенесены' ? (
                    <LinkButton.Root variant='primary' size='medium' underline asChild>
                      <Link to={АДРЕС_ПЕРЕНОСА}>Перенести остатки листом</Link>
                    </LinkButton.Root>
                  ) : undefined
                }
              />
            </div>
          }
        >
          {(данные) => (
            <ul className='flex flex-col divide-y divide-stroke-soft-200'>
              {данные.items.map((долг) => (
                <li key={долг.id} className='flex items-center justify-between gap-4 px-4 py-3'>
                  <div className='flex min-w-0 flex-col gap-0.5'>
                    <span className='text-label-sm text-text-strong-950'>
                      {долг.description ?? долг.debt_type_title}
                    </span>
                    <span className='text-paragraph-xs text-text-sub-600'>
                      Заведён {formatDateOnly(долг.created_at)}
                      {` · ${долг.schedule_note}`}
                    </span>
                  </div>
                  <div className='flex shrink-0 items-center gap-3'>
                    <Badge.Root
                      variant='lighter'
                      color={долг.is_blocking ? 'orange' : 'gray'}
                      size='medium'
                    >
                      {СОСТОЯНИЯ[долг.status] ?? долг.status}
                    </Badge.Root>
                    {/* Списание стоит В СТРОКЕ долга, а не общей кнопкой блока: списывают
                        конкретный долг, и общая кнопка потребовала бы сначала спросить
                        «какой». Действующие долги списываются, погашенные и уже списанные —
                        нет, и кнопки у них нет вовсе: списывать там нечего. */}
                    {долг.status === 'active' && долг.outstanding > 0 && (
                      <WriteOffDialog долг={долг} employeeId={employee.id} можно={можноСписать} />
                    )}
                    <div className='flex flex-col items-end'>
                      {/* Столбец сумм: знак рубля не повторяется у каждой строки —
                          единицу называет сам блок «Долги». В тексте рядом («удержание
                          1 000 ₽ в месяц») знак остаётся: там число стоит в фразе. */}
                      <span className='text-label-sm tabular-nums text-text-strong-950'>
                        {formatKopecks(долг.outstanding)}
                      </span>
                      <span className='text-paragraph-xs tabular-nums text-text-sub-600'>
                        из {formatKopecks(долг.initial_amount)}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </ScreenState>
      </Block>

      <Block
        flush
        title='Выданная форма'
        action={
          <Button.Root
            type='button'
            variant='neutral'
            mode='stroke'
            size='xsmall'
            onClick={() => {
              setОформляем((было) => !было);
              setЗамечание(null);
            }}
          >
            {оформляем ? 'Не выдавать' : 'Выдать форму'}
          </Button.Root>
        }
      >
        {оформляем ? (
          <div className='flex flex-col gap-3 rounded-xl border border-stroke-soft-200 bg-bg-white-0 px-4 py-3'>
            {/* У справочника четыре состояния, а не два. Пустой справочник и отвалившийся
                сервер выглядели бы одинаково — пустым местом под заголовком, — и человек
                решил бы, что «форма кончилась», хотя дело в связи. */}
            {справочник.isPending ? (
              <Skeleton className='h-24 w-full' />
            ) : справочник.error !== null ? (
              <div className='flex flex-col gap-1'>
                <span className='text-paragraph-sm text-text-strong-950'>
                  Справочник спецодежды не приехал
                </span>
                <button
                  type='button'
                  onClick={() => void справочник.refetch()}
                  className='w-fit text-label-xs text-primary-base'
                >
                  Повторить
                </button>
              </div>
            ) : позиции.length === 0 ? (
              <span className='text-paragraph-sm text-text-sub-600'>
                В справочнике нет ни одной позиции. Их заводит администратор в настройках — до этого
                выдавать нечего.
              </span>
            ) : (
              <ul className='flex flex-col gap-2'>
                {позиции.map((позиция) => (
                  <li key={позиция.id} className='flex items-center justify-between gap-3'>
                    <div className='flex min-w-0 flex-col'>
                      <span className='text-paragraph-sm text-text-strong-950'>
                        {позиция.title}
                      </span>
                      <span className='text-paragraph-xs text-text-sub-600'>
                        {formatRubles(позиция.price)} за штуку
                      </span>
                    </div>
                    <div className='flex shrink-0 items-center gap-2'>
                      <Button.Root
                        type='button'
                        variant='neutral'
                        mode='ghost'
                        size='xsmall'
                        onClick={() => изменить(позиция.id, -1)}
                      >
                        −
                      </Button.Root>
                      <span className='w-6 text-center text-label-sm text-text-strong-950'>
                        {набор[позиция.id] ?? 0}
                      </span>
                      <Button.Root
                        type='button'
                        variant='neutral'
                        mode='ghost'
                        size='xsmall'
                        onClick={() => изменить(позиция.id, 1)}
                      >
                        +
                      </Button.Root>
                    </div>
                  </li>
                ))}
              </ul>
            )}

            {/* Покупная позиция: куртка, купленная в магазине, а не взятая со склада. Сценарий
                43 требует её прямо. Цена вводится руками — справочной цены у неё нет по
                определению, иначе она была бы позицией справочника. */}
            <div className='flex flex-col gap-2 border-t border-stroke-soft-200 pt-3'>
              {своиПозиции.map((своя, номер) => (
                <div key={своя.id} className='flex items-end gap-2'>
                  <div className='min-w-0 flex-1'>
                    <TextField
                      id={`uniform-manual-title-${своя.id}`}
                      label={номер === 0 ? 'Покупная позиция' : ''}
                      value={своя.название}
                      onChange={(значение) =>
                        setСвоиПозиции((прежние) =>
                          прежние.map((строка) =>
                            строка.id === своя.id ? { ...строка, название: значение } : строка,
                          ),
                        )
                      }
                      placeholder='куртка зимняя, куплена в магазине'
                    />
                  </div>
                  <div className='w-28'>
                    <TextField
                      id={`uniform-manual-price-${своя.id}`}
                      label={номер === 0 ? 'Цена' : ''}
                      value={своя.цена}
                      onChange={(значение) =>
                        setСвоиПозиции((прежние) =>
                          прежние.map((строка) =>
                            строка.id === своя.id ? { ...строка, цена: значение } : строка,
                          ),
                        )
                      }
                      placeholder='2 500'
                      inputMode='decimal'
                    />
                  </div>
                  <Button.Root
                    type='button'
                    variant='neutral'
                    mode='stroke'
                    size='xsmall'
                    onClick={() =>
                      setСвоиПозиции((прежние) => прежние.filter((строка) => строка.id !== своя.id))
                    }
                  >
                    Убрать
                  </Button.Root>
                </div>
              ))}
              <Button.Root
                type='button'
                variant='neutral'
                mode='stroke'
                size='xsmall'
                className='self-start'
                onClick={() =>
                  setСвоиПозиции((прежние) => [
                    ...прежние,
                    {
                      id: `своя-${прежние.length + 1}-${Date.now()}`,
                      название: '',
                      цена: '',
                      количество: 1,
                    },
                  ])
                }
              >
                Добавить покупную позицию
              </Button.Root>
            </div>

            <div className='flex items-center justify-between gap-3 border-t border-stroke-soft-200 pt-3'>
              <div className='flex flex-col'>
                <span className='tabular-nums text-label-sm text-text-strong-950'>
                  {formatRubles(итог)}
                </span>
                <span className='text-paragraph-xs text-text-sub-600'>
                  Заведётся долг на эту сумму, удержание тысяча в месяц
                </span>
              </div>
              <Button.Root
                type='button'
                variant='primary'
                mode='filled'
                size='xsmall'
                onClick={() => {
                  // Пока идёт первая попытка, вторая не начинается: у неё был бы свой ключ
                  // операции, и сервер посчитал бы её новой выдачей — два долга на одну
                  // куртку. Кнопка при этом не сереет, а говорит, что уже оформляет.
                  if (выдать.isPending) {
                    setЗамечание('Выдача уже оформляется, ждём ответ сервера.');
                    return;
                  }
                  void оформить();
                }}
              >
                {выдать.isPending ? 'Оформляем…' : 'Оформить выдачу'}
              </Button.Root>
            </div>
            {замечание === null ? null : <Hint.Root>{замечание}</Hint.Root>}
          </div>
        ) : null}

        <ScreenState
          data={выдачи.data}
          error={выдачи.error as ApiError | null}
          subject='выдачи формы'
          isEmpty={(данные) => данные.items.length === 0}
          onRetry={() => void выдачи.refetch()}
          loading={<Skeleton className='h-16 w-full' />}
          empty={
            <div className='p-5'>
              {/* «Форму не выдавали» было неправдой ровно так же, как «долгов нет»: выдачи до
                  начала работы в программе сюда не переносятся вовсе, а форму человек носит. */}
              <EmptyState
                icon={RiTShirtLine}
                title='В программе этому человеку форму не выдавали'
                description='Здесь видно, что и когда человек получил через программу, с ценами на момент выдачи. Форма, выданная до начала работы в программе, сюда не переносится.'
              />
            </div>
          }
        >
          {(данные) => (
            <ul className='flex flex-col gap-2'>
              {данные.items.map((выдача) => (
                <li
                  key={выдача.id}
                  className='flex flex-col gap-1 rounded-xl border border-stroke-soft-200 bg-bg-white-0 px-4 py-3'
                >
                  <div className='flex items-baseline justify-between gap-3'>
                    <span className='text-label-sm text-text-strong-950'>
                      {formatDateOnly(выдача.issued_at)}
                    </span>
                    <span className='text-label-sm tabular-nums text-text-strong-950'>
                      {formatKopecks(выдача.total_amount)}
                    </span>
                  </div>
                  <span className='text-paragraph-xs text-text-sub-600'>
                    {выдача.lines
                      .map((позиция) =>
                        позиция.quantity > 1
                          ? `${позиция.title} × ${позиция.quantity}`
                          : позиция.title,
                      )
                      .join(', ')}
                  </span>
                  <span className='text-paragraph-xs text-text-sub-600'>
                    {countLabel(выдача.lines.length, ['позиция', 'позиции', 'позиций'])}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </ScreenState>
      </Block>
    </div>
  );
}

/**
 * Заведение долга вручную (сценарий приёмки 41).
 *
 * ## Почему график — поле, а не подпись
 *
 * Долг за форму удерживают по тысяче в месяц, билет по пятьсот, а штраф целиком и сразу.
 * Подставить одно значение молча значит списать с человека не то, о чём с ним договорились.
 *
 * С 15.09.2026 спрашивается не только сумма, но и СПОСОБ: тз (5.5) обещает пять, и до этого
 * дня форма умела ровно один. Договорённость «отдаю за полгода» записать было нечем — её
 * приходилось переводить в сумму за расчёт руками, а потом никто, включая самого человека,
 * не знал, когда долг кончится.
 *
 * Поля способа и значения общие с карточкой долга (`shared/debts/ПоляГрафика`): единица
 * значения зависит от способа, и второй разбор рядом однажды отправил бы месяцы копейками.
 *
 * ## Почему основание обязательно
 *
 * Долг уменьшает то, что человек получит на руки. Запись без единого слова о том, откуда она
 * взялась, на разборе через месяц не объяснить никому — ни человеку, ни финдиректору.
 */
function ФормаДолга({ employee, onГотово }: { employee: EmployeeOut; onГотово: () => void }) {
  const типы = useDebtTypes();
  const способы = useDebtScheduleTypes();
  const юрлица = useLegalEntitiesQuery();
  const завести = useCreateDebt(employee.id);

  const [тип, setТип] = useState(NO_VALUE);
  const [юрлицо, setЮрлицо] = useState(NO_VALUE);
  const [сумма, setСумма] = useState('');
  const [график, setГрафик] = useState<СостояниеГрафика>({
    // Способ подставлен сразу: «фиксированная сумма в месяц» — это девять долгов из десяти
    // у заказчика, и заставлять выбирать его каждый раз значит добавить нажатие к самому
    // частому пути ради самого редкого (`ПРАВИЛА.md`, про справочник с одним вариантом).
    ...ПУСТОЙ_ГРАФИК,
    тип: 'per_payroll',
  });
  const [основание, setОснование] = useState('');
  const [ошибки, setОшибки] = useState<Record<string, string>>({});

  return (
    <form
      className='border-b border-stroke-soft-200 p-5'
      noValidate
      onSubmit={(событие) => {
        событие.preventDefault();
        const next: Record<string, string> = {};
        if (тип === NO_VALUE) {
          next.тип = 'Выберите тип: от него зависит, в какой колонке ведомости встанет удержание.';
        }
        if (юрлицо === NO_VALUE) {
          next.юрлицо = 'Выберите юрлицо: долг удерживается из его ведомости.';
        }
        const разобранная = parseRublesToKopecks(сумма);
        if (сумма.trim() === '') {
          // Пустое поле и «пять тыщ» — разные ошибки. Первому нужно напоминание, второму
          // образец; общая фраза про формат на пустом поле выглядит придиркой.
          next.сумма = 'Укажите сумму: без неё удерживать нечего.';
        } else if (!разобранная.ok) {
          next.сумма = 'Сумма пишется числом: 5 000 или 5 000,50.';
        } else if (разобранная.value <= 0) {
          next.сумма = 'Ноль не долг: заводить нечего.';
        }
        // Разбор графика общий с карточкой долга: правила единиц живут в одном месте, здесь
        // их нет ни одного.
        const разборГрафика = разобратьГрафик(график, способы.data?.items ?? []);
        if (!разборГрафика.ok) {
          next[разборГрафика.поле === 'тип' ? 'способ' : 'график'] = разборГрафика.ошибка;
        }
        if (основание.trim().length < 3) {
          next.основание =
            'Основание обязательно: удержание без причины через месяц не объяснить ни человеку, ни финдиректору.';
        }
        if (Object.keys(next).length > 0 || !разобранная.ok || !разборГрафика.ok) {
          setОшибки(next);
          return;
        }
        setОшибки({});
        завести.mutate(
          {
            employee_id: employee.id,
            legal_entity_id: юрлицо,
            debt_type: тип,
            initial_amount: разобранная.value,
            schedule_type: разборГрафика.schedule_type,
            schedule_value: разборГрафика.schedule_value,
            basis: основание.trim(),
          },
          { onSuccess: onГотово },
        );
      }}
    >
      {/* Ширина ограничена: поле «сумма долга» во всю ширину детали — это строка ввода на
          девятьсот точек под пять цифр. Форма перестаёт читаться как форма. Парами стоят те
          поля, которые и думаются парой: чем долг вызван и из чьей ведомости удержать;
          сколько всего и сколько за раз. */}
      <div className='flex max-w-lg flex-col gap-3'>
        <div className='grid grid-cols-2 gap-3'>
          <SelectField
            id='debt-type'
            label='Тип долга'
            required
            value={тип}
            onChange={setТип}
            options={(типы.data?.items ?? []).map((строка) => ({
              value: строка.code,
              label: строка.title,
            }))}
            placeholder='выберите тип'
            pending={типы.isPending}
            failed={типы.isError}
            failedError={типы.error as ApiError | null}
            emptyNote='Справочник типов долга пуст. Так не бывает: он приходит из контракта, значит система развёрнута не до конца.'
            error={ошибки.тип}
          />
          <SelectField
            id='debt-entity'
            label='Юрлицо'
            required
            value={юрлицо}
            onChange={setЮрлицо}
            options={(юрлица.data?.items ?? []).map((строка) => ({
              value: строка.id,
              label: строка.short_name ?? строка.name,
            }))}
            placeholder='выберите юрлицо'
            pending={юрлица.isPending}
            failed={юрлица.isError}
            failedError={юрлица.error as ApiError | null}
            emptyNote='Юрлиц в справочнике нет. Их заводит администратор в настройках системы.'
            hint='Удержим из ведомости этого юрлица.'
            error={ошибки.юрлицо}
          />
        </div>
        <div className='grid grid-cols-2 gap-3'>
          <TextField
            id='debt-amount'
            label='Сумма долга'
            required
            value={сумма}
            onChange={setСумма}
            placeholder='5 000'
            inputMode='decimal'
            error={ошибки.сумма}
          />
        </div>
        {/* Способ и его значение идут отдельной парой под суммой, а не рядом с ней: сумма
            долга и условия его гашения — два разных решения, и пара «сколько всего» и
            «сколько за раз» была верной, только пока способ был один. */}
        <ПоляГрафика
          id='debt-form'
          состояние={график}
          onChange={setГрафик}
          способы={способы.data}
          загружаются={способы.isPending}
          ошибкаСправочника={способы.error as ApiError | null}
          умолчаниеКопеек={
            (типы.data?.items ?? []).find((строка) => строка.code === тип)?.default_schedule_value
          }
          ошибки={{ тип: ошибки.способ, значение: ошибки.график }}
        />
        <TextField
          id='debt-basis'
          label='Основание'
          required
          value={основание}
          onChange={setОснование}
          placeholder='штраф за утерю пропуска, служебная записка от 12.08'
          hint='Останется в карточке долга: по нему через месяц объясняют человеку, за что удержали.'
          error={ошибки.основание}
        />

        {завести.isError && <ErrorAlert error={завести.error as never} />}

        <div className='flex items-center gap-2 pt-1'>
          <Button.Root type='submit' variant='primary' mode='filled' size='xsmall'>
            {завести.isPending ? 'Заводим…' : 'Завести долг'}
          </Button.Root>
          <Button.Root
            type='button'
            variant='neutral'
            mode='stroke'
            size='xsmall'
            onClick={onГотово}
          >
            Отмена
          </Button.Root>
        </div>
      </div>
    </form>
  );
}
